import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import useApiData from "../../services/useApiData.js";
import PostData from "../../services/PostData.js";
import PutData from "../../services/PutData.js";
import api from "../../services/axiosInterceptor.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import AuthenticatedAssetImage from "../Helpers/AuthenticatedAssetImage.jsx";
import GridRowContextMenu, {
    buildDimensionShareUrl,
    copyShareUrlAction,
    useGridRowContextMenu,
    useSyncedUrlSearchQuery,
} from "../Helpers/GridRowContextMenu.jsx";
import ConfirmDeleteDialog from "../Helpers/ConfirmDeleteDialog.jsx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { dimensionLayoutKey } from "../Helpers/gridLayoutKeys.js";
import { fieldsFromDimensionSpec } from "../Helpers/gridFilterFields.js";
import { DimensionMultiSelect, DimensionSingleSelect, LookupField, SkuTextField } from "./TypedFields.jsx";
import {
    catalogDataGridSx,
    catalogDangerButtonSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "./catalogGridStyles.js";

const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

function emptyForm(fields) {
    return Object.fromEntries(
        fields.map((field) => [
            field.key,
            field.type === "links" ||
            field.type === "customPacks" ||
            field.type === "photos"
                ? []
                : "",
        ])
    );
}

function emptyPhotoUploads(fields) {
    return Object.fromEntries(
        fields.filter((field) => field.type === "photos").map((field) => [field.key, []])
    );
}

function photoAssetsFromRaw(raw) {
    if (Array.isArray(raw)) {
        return raw.filter((asset) => asset?.gcsObject || asset?.sourceUrl);
    }
    return [];
}

function revokePhotoUploads(uploadsByField) {
    for (const list of Object.values(uploadsByField || {})) {
        for (const entry of list || []) {
            if (entry?.url) URL.revokeObjectURL(entry.url);
        }
    }
}

function linkedName(field, entry) {
    if (!entry || typeof entry !== "object") return "";
    if (field.optionFallback) {
        return [entry[field.optionLabel], entry[field.optionFallback]].filter(Boolean).join(" · ");
    }
    return entry[field.optionLabel] || entry.name || entry.internalName || "";
}

function formatCustomPackSummary(packs) {
    if (!Array.isArray(packs) || !packs.length) return "";
    return packs
        .map((pack) => {
            const name = String(pack?.name || "").trim();
            if (!name) return "";
            const dims = [pack.length, pack.width, pack.height]
                .filter((value) => value != null && value !== "")
                .join("×");
            const weight =
                pack.weight != null && pack.weight !== "" ? `${pack.weight} lbs` : "";
            const bits = [weight, dims].filter(Boolean).join(" · ");
            return bits ? `${name} (${bits})` : name;
        })
        .filter(Boolean)
        .join("; ");
}

function customPacksFormValue(raw) {
    return (Array.isArray(raw?.customPacks) ? raw.customPacks : []).map((pack) => ({
        name: String(pack?.name || "").trim(),
        weight: pack?.weight == null ? "" : String(pack.weight),
        length: pack?.length == null ? "" : String(pack.length),
        width: pack?.width == null ? "" : String(pack.width),
        height: pack?.height == null ? "" : String(pack.height),
        config: pack?.config?._id || pack?.config || "",
    }));
}

function formValue(field, raw) {
    if (field.type === "customPacks") return customPacksFormValue(raw);
    if (field.type === "photos") return photoAssetsFromRaw(raw?.[field.key]);
    if (field.type === "links") {
        const linked = Array.isArray(raw?.[field.key]) ? raw[field.key] : [];
        return linked.map((item) => item?._id || item).filter(Boolean);
    }
    if (field.type === "link") {
        const value = raw?.[field.key];
        return value?._id || value || "";
    }
    if (field.type === "number") {
        const value = raw?.[field.key];
        return value == null ? "" : String(value);
    }
    return raw?.[field.key] || "";
}

function cellValue(field, item) {
    if (field.type === "customPacks") return formatCustomPackSummary(item?.[field.key]);
    if (field.type === "photos") {
        const assets = photoAssetsFromRaw(item?.[field.key]);
        if (!assets.length) return "";
        return `${assets.length} photo${assets.length === 1 ? "" : "s"}`;
    }
    if (field.type === "links") {
        const linked = Array.isArray(item?.[field.key]) ? item[field.key] : [];
        return linked.map((entry) => linkedName(field, entry)).filter(Boolean).join(", ");
    }
    if (field.type === "link") return linkedName(field, item?.[field.key]);
    if (field.type === "number") return item?.[field.key] ?? "";
    return item?.[field.key] || "";
}

function displayValue(row, column) {
    if (column.value) return column.value(row);
    const value = row?.[column.key];
    return value == null ? "" : value;
}

function LinkOptionLoader({ endpoint, onLoad }) {
    const listUrl = useMemo(() => {
        if (!endpoint) return "";
        const joiner = endpoint.includes("?") ? "&" : "?";
        return `${endpoint}${joiner}page=1&pageSize=500`;
    }, [endpoint]);
    const { data } = useApiData(listUrl, { enabled: Boolean(listUrl) });
    useEffect(() => {
        onLoad(endpoint, data?.items || []);
    }, [endpoint, data, onLoad]);
    return null;
}

/** Denormalized read-only fields that still live on the Mongo document. */
const SORTABLE_READONLY_KEYS = new Set([
    "label",
    "internalSkuId",
    "costPerUnit",
    "costRollup",
    "costPerUnitRollupFromSingleUseMaterials",
    "totalConfigurationCost",
    "totalTapeCost",
    "tapeCostPerInch",
    "tapeNeededInches",
    "boxLength",
]);

function defaultSortFieldForSpec(spec) {
    const fields = spec?.fields || [];
    for (const key of ["name", "label", "internalName"]) {
        if (fields.some((field) => field.key === key)) return key;
    }
    const first = fields.find(
        (field) =>
            field.type !== "link" &&
            field.type !== "links" &&
            field.type !== "customPacks" &&
            !(field.readOnly && !SORTABLE_READONLY_KEYS.has(field.key))
    );
    return first?.key || "name";
}

function columnIsSortable(field) {
    if (
        field.type === "link" ||
        field.type === "links" ||
        field.type === "customPacks" ||
        field.type === "photos"
    ) {
        return false;
    }
    if (field.readOnly && !SORTABLE_READONLY_KEYS.has(field.key)) {
        return false;
    }
    return true;
}

export default function DimensionGrid({ spec }) {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const fields = spec.fields;
    const defaultSortField = useMemo(() => defaultSortFieldForSpec(spec), [spec]);
    const defaultSortModel = useMemo(
        () => [{ field: defaultSortField, sort: "asc" }],
        [defaultSortField]
    );
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const { searchInput, setSearchInput, searchQ } = useSyncedUrlSearchQuery();
    const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(() => emptyForm(fields));
    const [photoUploads, setPhotoUploads] = useState(() => emptyPhotoUploads(fields));
    const photoInputRefs = useRef({});
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const {
        menu: rowMenu,
        close: closeRowMenu,
        rowContextMenuSlotProps,
    } = useGridRowContextMenu();

    const selectedIds = useMemo(
        () => [...(selectionModel?.ids ?? [])],
        [selectionModel]
    );

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [searchQ]);

    const shareTab = searchParams.get("t") || "";
    const rowMenuActions = useMemo(
        () => [
            copyShareUrlAction({
                buildUrl: (row) =>
                    buildDimensionShareUrl(row, {
                        pathname: location.pathname,
                        tab: shareTab || (location.pathname.startsWith("/products") ? "1" : ""),
                    }),
                disabled: (row) =>
                    !String(
                        row?.label || row?.internalSkuId || row?.name || row?.upc || ""
                    ).trim(),
                onCopied: () =>
                    setSnack({
                        open: true,
                        color: "success",
                        message: "Link copied.",
                    }),
            }),
        ],
        [location.pathname, shareTab]
    );

    const filterFields = useMemo(() => fieldsFromDimensionSpec(spec), [spec]);
    const {
        activeConditions,
        conjunction,
        barProps: filterBar,
    } = useGridFilter(filterFields);

    const filtersParam = useMemo(() => {
        if (!activeConditions.length) return "";
        return JSON.stringify({
            conjunction,
            conditions: activeConditions.map(({ field, operator, value }) => ({
                field,
                operator,
                value,
            })),
        });
    }, [activeConditions, conjunction]);

    const layout = useGridLayout(dimensionLayoutKey(spec), {
        sortModel: defaultSortModel,
        columnVisibilityModel: {},
    });

    const sortForApi = useMemo(() => {
        const entry = layout.sortModel?.[0];
        if (entry?.field) {
            return {
                sort: entry.field,
                sortDir: entry.sort === "asc" ? "asc" : "desc",
            };
        }
        return { sort: defaultSortField, sortDir: "asc" };
    }, [layout.sortModel, defaultSortField]);

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [filtersParam, conjunction, searchQ, sortForApi.sort, sortForApi.sortDir]);

    const listUrl = useMemo(() => {
        const params = new URLSearchParams({
            page: String(paginationModel.page + 1),
            pageSize: String(paginationModel.pageSize),
            sort: sortForApi.sort,
            sortDir: sortForApi.sortDir,
        });
        if (searchQ) params.set("q", searchQ);
        if (filtersParam) params.set("filters", filtersParam);
        for (const [key, value] of Object.entries(spec.queryParams || {})) {
            if (value == null || value === "") continue;
            params.set(key, String(value));
        }
        return `api/${spec.endpoint}?${params.toString()}`;
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        searchQ,
        filtersParam,
        sortForApi.sort,
        sortForApi.sortDir,
        spec.endpoint,
        spec.queryParams,
    ]);

    const { data, error, load, isLoading, refetch } = useApiData(listUrl);

    const linkEndpoints = useMemo(() => {
        const endpoints = [];
        for (const field of fields) {
            if (
                (field.type === "links" ||
                    field.type === "link" ||
                    field.type === "customPacks") &&
                field.optionsEndpoint &&
                !endpoints.includes(field.optionsEndpoint)
            ) {
                endpoints.push(field.optionsEndpoint);
            }
        }
        return endpoints;
    }, [fields]);
    const [itemsByEndpoint, setItemsByEndpoint] = useState({});
    const onLinkOptions = useCallback((endpoint, items) => {
        setItemsByEndpoint((prev) => (prev[endpoint] === items ? prev : { ...prev, [endpoint]: items }));
    }, []);
    const optionsFor = (field) =>
        (itemsByEndpoint[field.optionsEndpoint] || []).map((item) => ({
            _id: item._id,
            name: linkedName(field, item),
        }));

    useEffect(() => {
        if (data == null) return;
        const items = Array.isArray(data.items) ? data.items : [];
        setRowCount(typeof data.total === "number" ? data.total : items.length);
        setRows(
            items.map((item) => {
                const row = { id: item._id, raw: item };
                for (const field of fields) {
                    row[field.key] = field.readOnly ? displayValue(item, field) : cellValue(field, item);
                }
                for (const column of spec.readOnly || []) {
                    row[column.key] = displayValue(item, column);
                }
                return row;
            })
        );
    }, [data, fields, spec.readOnly]);

    const gridSlotProps = useMemo(
        () => rowContextMenuSlotProps(rows, appDataGridSlotProps),
        [rowContextMenuSlotProps, rows]
    );

    const baseColumns = useMemo(() => {
        const editable = fields.map((field) => ({
            field: field.key,
            headerName: field.label,
            flex: 1,
            minWidth: field.readOnly ? 140 : 160,
            sortable: columnIsSortable(field),
        }));
        const locked = (spec.readOnly || []).map((column) => ({
            field: column.key,
            headerName: column.label,
            flex: 0.6,
            minWidth: 140,
            sortable: SORTABLE_READONLY_KEYS.has(column.key),
        }));
        return [...editable, ...locked];
    }, [fields, spec.readOnly]);

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.draftWidths, layout.applyWidths]
    );

    const closeDialog = () => {
        setDialogOpen(false);
        revokePhotoUploads(photoUploads);
        setPhotoUploads(emptyPhotoUploads(fields));
    };

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm(fields));
        revokePhotoUploads(photoUploads);
        setPhotoUploads(emptyPhotoUploads(fields));
        setDialogOpen(true);
    };

    const openEdit = (idOverride) => {
        const id = idOverride ?? selectedIds[0];
        const row = rows.find((item) => item.id === id);
        if (!row) return;
        setEditingId(id);
        setForm(
            Object.fromEntries(
                fields
                    .filter((field) => !field.readOnly)
                    .map((field) => [field.key, formValue(field, row.raw)])
            )
        );
        revokePhotoUploads(photoUploads);
        setPhotoUploads(emptyPhotoUploads(fields));
        setDialogOpen(true);
    };

    const onPhotosSelected = (field, event) => {
        const maxCount = Math.max(1, Number(field.maxCount) || 20);
        const picked = Array.from(event.target.files || []).filter((file) =>
            String(file.type || "").startsWith("image/")
        );
        event.target.value = "";
        if (!picked.length) return;

        // Single-photo fields replace existing + pending.
        if (maxCount === 1) {
            const file = picked[0];
            setForm((prev) => ({ ...prev, [field.key]: [] }));
            setPhotoUploads((prev) => {
                for (const entry of prev[field.key] || []) {
                    if (entry?.url) URL.revokeObjectURL(entry.url);
                }
                return {
                    ...prev,
                    [field.key]: [
                        {
                            key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
                            file,
                            url: URL.createObjectURL(file),
                        },
                    ],
                };
            });
            return;
        }

        setPhotoUploads((prev) => {
            const existingKept = Array.isArray(form[field.key]) ? form[field.key].length : 0;
            const currentPending = prev[field.key] || [];
            const room = Math.max(0, maxCount - existingKept - currentPending.length);
            if (room <= 0) return prev;
            const nextFiles = picked.slice(0, room).map((file) => ({
                key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
                file,
                url: URL.createObjectURL(file),
            }));
            return { ...prev, [field.key]: [...currentPending, ...nextFiles] };
        });
    };

    const removeExistingPhoto = (fieldKey, index) => {
        setForm((prev) => ({
            ...prev,
            [fieldKey]: (prev[fieldKey] || []).filter((_, i) => i !== index),
        }));
    };

    const removePendingPhoto = (fieldKey, entryKey) => {
        setPhotoUploads((prev) => {
            const list = prev[fieldKey] || [];
            const target = list.find((entry) => entry.key === entryKey);
            if (target?.url) URL.revokeObjectURL(target.url);
            return {
                ...prev,
                [fieldKey]: list.filter((entry) => entry.key !== entryKey),
            };
        });
    };

    const save = async () => {
        const required = fields.find((field) => {
            if (!field.required) return false;
            const value = form[field.key];
            if (field.readOnly) return false;
            if (field.type === "photos") return false;
            if (field.type === "links") return !value?.length;
            if (field.type === "link") return !value;
            return !String(value || "").trim();
        });
        if (required) {
            setSnack({ open: true, color: "error", message: `${required.label} is required.` });
            return;
        }
        setSaving(true);
        const payload = Object.fromEntries(
            fields.filter((field) => !field.readOnly).map((field) => {
                if (field.type === "photos") {
                    return [field.key, Array.isArray(form[field.key]) ? form[field.key] : []];
                }
                if (field.type === "customPacks") {
                    const packs = (form[field.key] || [])
                        .map((pack) => {
                            const name = String(pack?.name || "").trim();
                            if (!name) return null;
                            const toNum = (value) => {
                                if (value === "" || value == null) return null;
                                const number = Number(value);
                                return Number.isFinite(number) ? number : null;
                            };
                            return {
                                name,
                                weight: toNum(pack.weight),
                                length: toNum(pack.length),
                                width: toNum(pack.width),
                                height: toNum(pack.height),
                                config: pack.config || null,
                            };
                        })
                        .filter(Boolean);
                    return [field.key, packs];
                }
                if (field.type === "links") return [field.key, form[field.key] || []];
                if (field.type === "link") return [field.key, form[field.key] || null];
                if (field.type === "number") {
                    if (form[field.key] === "" || form[field.key] == null) return [field.key, null];
                    const number = Number(form[field.key]);
                    return [field.key, Number.isFinite(number) ? number : null];
                }
                return [field.key, String(form[field.key] || "").trim()];
            })
        );

        const photoFields = fields.filter((field) => field.type === "photos");
        const hasNewPhotos = photoFields.some(
            (field) => (photoUploads[field.key] || []).length > 0
        );

        let result;
        if (hasNewPhotos) {
            const formData = new FormData();
            for (const [key, value] of Object.entries(payload)) {
                if (value == null) continue;
                if (Array.isArray(value) || typeof value === "object") {
                    formData.append(key, JSON.stringify(value));
                } else if (typeof value === "boolean") {
                    formData.append(key, value ? "true" : "false");
                } else {
                    formData.append(key, String(value));
                }
            }
            for (const field of photoFields) {
                for (const entry of photoUploads[field.key] || []) {
                    formData.append(field.key, entry.file, entry.file.name);
                }
            }
            try {
                const url = editingId
                    ? `api/${spec.endpoint}/${editingId}`
                    : `api/${spec.endpoint}`;
                const response = editingId
                    ? await api.put(url, formData)
                    : await api.post(url, formData);
                result = { success: true, data: response.data };
            } catch (err) {
                result = { success: false, error: err };
            }
        } else {
            result = editingId
                ? await PutData(payload, `${spec.endpoint}/${editingId}`)
                : await PostData(payload, spec.endpoint);
        }

        setSaving(false);
        if (result.success) {
            closeDialog();
            setSnack({
                open: true,
                color: "success",
                message: editingId ? "Record updated." : "Record created.",
            });
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } else {
            setSnack({
                open: true,
                color: "error",
                message:
                    result?.data?.message ||
                    result?.error?.response?.data?.message ||
                    "Save failed.",
            });
        }
    };

    const deleteTargets = useMemo(() => {
        const idSet = new Set(selectedIds.map(String));
        return rows
            .filter((row) => idSet.has(String(row.id)))
            .map((row) => ({
                key: String(row.id),
                label:
                    row.name ||
                    row.internalName ||
                    row.code ||
                    String(row.id),
            }));
    }, [rows, selectedIds]);

    const confirmDelete = async () => {
        if (!selectedIds.length) return;
        setDeleting(true);
        try {
            await Promise.all(selectedIds.map((id) => api.delete(`api/${spec.endpoint}/${id}`)));
            setDeleteDialogOpen(false);
            setSnack({ open: true, color: "success", message: "Deleted." });
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } catch (err) {
            setSnack({
                open: true,
                color: "error",
                message: err?.response?.data?.message || "Delete failed.",
            });
        } finally {
            setDeleting(false);
        }
    };

    const editingRaw = rows.find((row) => row.id === editingId)?.raw;

    return (
        <>
            {linkEndpoints.map((endpoint) => (
                <LinkOptionLoader key={endpoint} endpoint={endpoint} onLoad={onLinkOptions} />
            ))}
            {error ? (
                <Typography color="error" sx={{ mb: 1, fontSize: 13, px: 2 }}>
                    Failed to load records.
                </Typography>
            ) : null}
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder={`Search ${spec.title.toLowerCase()}…`}
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {selectedIds.length > 0 ? (
                        <span className="catalog-selection-hint">{selectedIds.length} selected</span>
                    ) : null}
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedIds.length || deleting}
                        startIcon={
                            deleting ? (
                                <CircularProgress size={14} color="error" />
                            ) : (
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={catalogDangerButtonSx}
                    >
                        Delete
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={selectedIds.length !== 1}
                        startIcon={<EditOutlinedIcon fontSize="small" />}
                        onClick={() => openEdit()}
                        sx={catalogQuietButtonSx}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddOutlinedIcon fontSize="small" />}
                        onClick={openCreate}
                        sx={catalogPrimaryButtonSx}
                    >
                        Add
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(load || isLoading)}
                        rows={rows}
                        columns={columns}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        sortingMode="server"
                        sortModel={
                            layout.sortModel?.length ? layout.sortModel : defaultSortModel
                        }
                        onSortModelChange={(model) => {
                            layout.onSortModelChange(
                                model?.length ? model : defaultSortModel
                            );
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        onRowDoubleClick={(params) => openEdit(params.id)}
                        paginationMode="server"
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        slotProps={gridSlotProps}
                        sx={catalogDataGridSx}
                    />
                </div>
            </div>

            <GridRowContextMenu
                menu={rowMenu}
                onClose={closeRowMenu}
                actions={rowMenuActions}
            />

            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                maxWidth={spec.maxWidth || "xs"}
                fullWidth
                PaperProps={{ sx: { borderRadius: "14px" } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {editingId ? `Edit ${spec.singular}` : `Add ${spec.singular}`}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
                        {fields.map((field) =>
                            field.readOnly ? (
                                <LookupField
                                    key={field.key}
                                    label={field.label}
                                    value={editingRaw ? displayValue(editingRaw, field) : ""}
                                    source={field.source}
                                />
                            ) : field.type === "photos" ? (
                                <Box
                                    key={field.key}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        p: 1.5,
                                        borderRadius: "12px",
                                        border: "1px solid #e2e8f0",
                                        backgroundColor: "#f8fafc",
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                                        {field.label}
                                    </Typography>
                                    {field.hint ? (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mt: -0.5 }}
                                        >
                                            {field.hint}
                                        </Typography>
                                    ) : null}
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                        {(form[field.key] || []).map((asset, index) => (
                                            <Box
                                                key={asset.gcsObject || asset.sourceUrl || index}
                                                sx={{ position: "relative", width: 72, height: 72 }}
                                            >
                                                <AuthenticatedAssetImage
                                                    asset={asset}
                                                    alt={`${field.label} ${index + 1}`}
                                                    sx={{
                                                        width: 72,
                                                        height: 72,
                                                        borderRadius: "10px",
                                                        border: "1px solid #e2e8f0",
                                                        backgroundColor: "#fff",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        removeExistingPhoto(field.key, index)
                                                    }
                                                    sx={{
                                                        position: "absolute",
                                                        top: -8,
                                                        right: -8,
                                                        backgroundColor: "#fff",
                                                        border: "1px solid #e2e8f0",
                                                        "&:hover": { backgroundColor: "#f8fafc" },
                                                    }}
                                                >
                                                    <CloseOutlinedIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Box>
                                        ))}
                                        {(photoUploads[field.key] || []).map((entry) => (
                                            <Box
                                                key={entry.key}
                                                sx={{ position: "relative", width: 72, height: 72 }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={entry.url}
                                                    alt={entry.file.name}
                                                    sx={{
                                                        width: 72,
                                                        height: 72,
                                                        objectFit: "cover",
                                                        borderRadius: "10px",
                                                        border: "1px solid #e2e8f0",
                                                        backgroundColor: "#fff",
                                                    }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        removePendingPhoto(field.key, entry.key)
                                                    }
                                                    sx={{
                                                        position: "absolute",
                                                        top: -8,
                                                        right: -8,
                                                        backgroundColor: "#fff",
                                                        border: "1px solid #e2e8f0",
                                                        "&:hover": { backgroundColor: "#f8fafc" },
                                                    }}
                                                >
                                                    <CloseOutlinedIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Box>
                                        ))}
                                        {!((form[field.key] || []).length ||
                                            (photoUploads[field.key] || []).length) ? (
                                            <Box
                                                sx={{
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: "10px",
                                                    border: "1px dashed #cbd5e1",
                                                    backgroundColor: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#94a3b8",
                                                }}
                                            >
                                                <ImageOutlinedIcon fontSize="small" />
                                            </Box>
                                        ) : null}
                                    </Box>
                                    <input
                                        ref={(el) => {
                                            photoInputRefs.current[field.key] = el;
                                        }}
                                        type="file"
                                        accept={IMAGE_ACCEPT}
                                        multiple={Number(field.maxCount) !== 1}
                                        hidden
                                        onChange={(event) => onPhotosSelected(field, event)}
                                    />
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        sx={{ ...catalogQuietButtonSx, alignSelf: "flex-start" }}
                                        onClick={() =>
                                            photoInputRefs.current[field.key]?.click()
                                        }
                                    >
                                        {Number(field.maxCount) === 1
                                            ? (form[field.key] || []).length ||
                                              (photoUploads[field.key] || []).length
                                                ? "Replace photo"
                                                : "Upload photo"
                                            : "Add photos"}
                                    </Button>
                                </Box>
                            ) : field.type === "customPacks" ? (
                                <Box key={field.key} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        {field.label}
                                    </Typography>
                                    {field.hint ? (
                                        <Typography variant="caption" color="text.secondary">
                                            {field.hint}
                                        </Typography>
                                    ) : null}
                                    {(form[field.key] || []).length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            None yet. Add from Shipping → Save as pack type.
                                        </Typography>
                                    ) : (
                                        (form[field.key] || []).map((pack, index) => (
                                            <Box
                                                key={`${pack.name}-${index}`}
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 1,
                                                    p: 1.5,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {pack.name}
                                                </Typography>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                    {["weight", "length", "width", "height"].map((dim) => (
                                                        <Box key={dim} sx={{ flex: "1 1 110px" }}>
                                                            <SkuTextField
                                                                label={dim.charAt(0).toUpperCase() + dim.slice(1)}
                                                                type="number"
                                                                value={pack[dim] ?? ""}
                                                                onChange={(event) => {
                                                                    const value = event.target.value;
                                                                    setForm((prev) => {
                                                                        const packs = [...(prev[field.key] || [])];
                                                                        packs[index] = {
                                                                            ...packs[index],
                                                                            [dim]: value,
                                                                        };
                                                                        return { ...prev, [field.key]: packs };
                                                                    });
                                                                }}
                                                            />
                                                        </Box>
                                                    ))}
                                                </Box>
                                                <DimensionSingleSelect
                                                    label={`${pack.name} Config`}
                                                    valueId={pack.config || ""}
                                                    options={optionsFor(field)}
                                                    onChange={(id) => {
                                                        setForm((prev) => {
                                                            const packs = [...(prev[field.key] || [])];
                                                            packs[index] = {
                                                                ...packs[index],
                                                                config: id || "",
                                                            };
                                                            return { ...prev, [field.key]: packs };
                                                        });
                                                    }}
                                                />
                                            </Box>
                                        ))
                                    )}
                                </Box>
                            ) : field.type === "links" ? (
                                <DimensionMultiSelect
                                    key={field.key}
                                    label={field.label}
                                    hint={field.hint}
                                    valueIds={form[field.key] || []}
                                    options={optionsFor(field)}
                                    onChange={(ids) =>
                                        setForm((prev) => ({ ...prev, [field.key]: ids }))
                                    }
                                />
                            ) : field.type === "link" ? (
                                <DimensionSingleSelect
                                    key={field.key}
                                    label={field.label}
                                    hint={field.hint}
                                    valueId={form[field.key] || ""}
                                    options={optionsFor(field)}
                                    onChange={(id) =>
                                        setForm((prev) => ({ ...prev, [field.key]: id || "" }))
                                    }
                                />
                            ) : (
                                <SkuTextField
                                    key={field.key}
                                    label={field.label}
                                    type={field.type === "number" ? "number" : "text"}
                                    multiline={field.type === "notes"}
                                    minRows={field.type === "notes" ? 2 : undefined}
                                    required={Boolean(field.required)}
                                    value={form[field.key] ?? ""}
                                    onChange={(event) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            [field.key]: event.target.value,
                                        }))
                                    }
                                />
                            )
                        )}
                        {editingId
                            ? (spec.readOnly || []).map((column) => (
                                  <LookupField
                                      key={column.key}
                                      label={column.label}
                                      value={displayValue(editingRaw, column)}
                                      source={column.source}
                                  />
                              ))
                            : null}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={closeDialog} sx={catalogQuietButtonSx}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={save}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={catalogPrimaryButtonSx}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                confirming={deleting}
                title={
                    deleteTargets.length === 1
                        ? `Delete ${String(spec.singular || "record").toLowerCase()}?`
                        : `Delete ${deleteTargets.length} ${String(spec.title || "records").toLowerCase()}?`
                }
                description="This cannot be undone. The following will be removed:"
                items={deleteTargets}
            />

            {snack.open ? (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            ) : null}
        </>
    );
}
